import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import api from '../../api'; 
import { getProfilePictureUrl } from '../../utils/apiHelper';
import defaultProfilePic from '../../assets/defaultProfilePic.png'; 
import './EditProfile.css';
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import countryCodes from '../../data/country-codes.json';

const EditProfile = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const navigate = useNavigate();
  const { token, profile, logout } = useAuth(); 
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91', // Default country code
    profilePicture: null,
    interestedCategories: [],
    interestedSkills: [],
  });

  const [preview, setPreview] = useState(defaultProfilePic); 
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedParent, setSelectedParent] = useState('');
  const [selectedSkillGroup, setSelectedSkillGroup] = useState('');

  const popularSkills = {
    'Software': ['MERN Stack', 'ReactJS', 'MongoDB', 'Node.Js', 'Microsoft Power BI', 'Microsoft Excel', 'Tableau'],
    'Programming Languages': ['Python', 'JavaScript', 'Java', 'C++', 'C#', 'Go', 'Swift', 'Kotlin', 'TypeScript', 'Ruby'],
    'Web Development': ['HTML', 'CSS', 'Next.js', 'Angular', 'Vue.js', 'Django', 'Flask', 'Spring Boot', 'ASP.NET'],
    'Data Science': ['Machine Learning', 'Deep Learning', 'Pandas', 'NumPy', 'Scikit-learn', 'TensorFlow', 'Keras', 'PyTorch', 'R'],
    'Cloud Computing': ['AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes'],
    'Mobile Development': ['React Native', 'Flutter', 'Android Development', 'iOS Development'],
    'Cybersecurity': ['Ethical Hacking', 'Network Security', 'Penetration Testing', 'Security Analytics'],
    'Design': ['UI/UX Design', 'Figma', 'Adobe XD', 'Photoshop', 'Illustrator'],
    'Marketing': ['Digital Marketing', 'SEO', 'Content Marketing', 'Social Media Marketing', 'Email Marketing'],
    'Project Management': ['Agile', 'Scrum', 'PMP', 'Jira', 'Asana'],
    'Business': ['Financial Modeling', 'Business Strategy', 'Entrepreneurship', 'Sales', 'Negotiation'],
    'Human Resources': ['Recruitment', 'Talent Management', 'HR Analytics', 'Compensation & Benefits'],
    'Education': ['Instructional Design', 'Curriculum Development', 'E-learning'],
    'Writing': ['Copywriting', 'Technical Writing', 'Creative Writing', 'Grant Writing'],
    'Finance & Accounting': ['Bookkeeping', 'Financial Planning', 'Tax Preparation', 'Auditing'],
    'Legal': ['Legal Research', 'Contract Law', 'Intellectual Property'],
    'Art': ['Drawing', 'Painting', 'Sculpting', 'Digital Art'],
  };

  useEffect(() => {
    if (!token) {
      navigate('/login'); 
      return;
    }
  
  
    const fetchProfile = async () => {
      try {
        const response = await axios.get(api.getProfile, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        const { name, email, phone, countryCode, profilePicture, interestedCategories, interestedSkills } = response.data;
  
     
        setFormData({ 
          name, 
          email, 
          phone,
          countryCode: countryCode || '+91', // Default to +91 if not set
          profilePicture: null,
          interestedCategories: interestedCategories || [],
          interestedSkills: interestedSkills || [],
        });
        
       
        const profileImageUrl = getProfilePictureUrl(profilePicture, api.img, defaultProfilePic, name);
  
        setPreview(profileImageUrl); 
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to fetch profile data.');
      }
    };
  
    fetchProfile();
  }, [token, navigate]);

  // Fetch categories for interest selection
  useEffect(() => {
    const fetchCategories = async () => {
      if (!token) return;
      try {
        const response = await axios.get(api.getAllCategories, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cats = response.data.categories || response.data || [];
        const grouped = cats.map((cat) => {
          const subs = [];
          (cat.subcategories || []).forEach((sub) => {
            subs.push(sub.name);
            (sub.subcategories || []).forEach((subsub) => {
              subs.push(subsub.name);
            });
          });
          return { name: cat.name, subcategories: subs };
        });
        setCategories(grouped);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [token]);

  const toggleCategory = (categoryName) => {
    setFormData((prev) => {
      const selected = prev.interestedCategories;
      if (selected.includes(categoryName)) {
        return { ...prev, interestedCategories: selected.filter((c) => c !== categoryName) };
      }
      return { ...prev, interestedCategories: [...selected, categoryName] };
    });
    setSelectedParent('');
  };

  const toggleSkill = (skillName) => {
    setFormData((prev) => {
      const selected = prev.interestedSkills;
      if (selected.includes(skillName)) {
        return { ...prev, interestedSkills: selected.filter((s) => s !== skillName) };
      }
      return { ...prev, interestedSkills: [...selected, skillName] };
    });
    setSelectedSkillGroup('');
  };
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
      profilePicture: prevFormData.profilePicture, // Ensure profilePicture is not lost
    }));
  };
  

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, profilePicture: file });
      setPreview(URL.createObjectURL(file)); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedData = new FormData();
      updatedData.append('name', formData.name);
      updatedData.append('email', formData.email);
      updatedData.append('phone', formData.phone);
      updatedData.append('countryCode', formData.countryCode);
      updatedData.append('interestedCategories', JSON.stringify(formData.interestedCategories));
      updatedData.append('interestedSkills', JSON.stringify(formData.interestedSkills));
  
      if (formData.profilePicture instanceof File) {
        updatedData.append('profilePicture', formData.profilePicture);
      } else if (preview !== defaultProfilePic) {
        updatedData.append('profilePicture', preview.replace(api.img, '')); 
      }
  
      const response = await axios.put(api.updateProfile, updatedData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
  
      setSuccessMessage(response.data.message);
      setError('');
      navigate('/home');
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error?.response?.data?.message || 'An error occurred');
      setSuccessMessage('');
    }
  };
  
  
  


  if (!token) {
    return null; 
  }

  return (
    <div>
      <UsersTopbar />
      <div className="edit-profile-container">
             
        <form onSubmit={handleSubmit}>
          <button 
            className="edit-profile-close-button" 
            onClick={() => navigate('/home')}
            aria-label="Close"
            type="button"
          >
          ×
          </button>
          <h2>Edit Profile</h2>

          {error && <div className="error">{error}</div>}
          {successMessage && <div className="success">{successMessage}</div>}

          <div className="profile-picture-container">
            <label htmlFor="profilePicture" className="custom-file-input">
              <img
                src={preview}
                alt="Profile Preview"
                className="profile-pictures"
              />
              <div className="camera-icon">
                <span className="material-icons">photo_camera</span>
              </div>
            </label>
            <input
              id="profilePicture"
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full Name"
            required
          />
          <input
            type="email"
            name="email"
            value={formData.email}
            placeholder="Email"
            disabled
          />
          <div className="phone-input-group">
            <select
              name="countryCode"
              value={formData.countryCode}
              onChange={handleChange}
              className="country-code-select"
            >
              {countryCodes.map((country) => (
                <option key={country.code + country.name} value={country.code}>
                  {country.code} ({country.name})
                </option>
              ))}
            </select>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              className="phone-input"
              required
            />
          </div>

          {/* Interested Categories */}
          <div className="edit-profile-categories-section">
            <label className="edit-profile-categories-label">Interested Categories</label>
            <p className="edit-profile-categories-hint">Choose a category, then select subcategories</p>

            {/* Selected tags */}
            {formData.interestedCategories.length > 0 && (
              <div className="edit-profile-selected-tags">
                {formData.interestedCategories.map((item) => (
                  <span key={item} className="edit-profile-tag">
                    {item}
                    <button type="button" onClick={() => toggleCategory(item)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Parent category dropdown */}
            <select
              value={selectedParent}
              onChange={(e) => setSelectedParent(e.target.value)}
              className="edit-profile-category-dropdown"
            >
              <option value="">-- Select a category --</option>
              {categories.map((group) => (
                <option key={group.name} value={group.name}>{group.name}</option>
              ))}
            </select>

            {/* Subcategory chips for selected parent */}
            {selectedParent && (
              <div className="edit-profile-category-chips">
                {(categories.find((g) => g.name === selectedParent)?.subcategories || []).map((sub) => (
                  <button
                    key={sub}
                    type="button"
                    className={`edit-profile-category-chip ${
                      formData.interestedCategories.includes(sub) ? 'selected' : ''
                    }`}
                    onClick={() => toggleCategory(sub)}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Interested Skills */}
          <div className="edit-profile-categories-section">
            <label className="edit-profile-categories-label">Interested Skills</label>
            <p className="edit-profile-categories-hint">Choose a group, then select skills</p>

            {/* Selected skill tags */}
            {formData.interestedSkills.length > 0 && (
              <div className="edit-profile-selected-tags">
                {formData.interestedSkills.map((item) => (
                  <span key={item} className="edit-profile-tag edit-profile-tag-skill">
                    {item}
                    <button type="button" onClick={() => toggleSkill(item)}>&times;</button>
                  </span>
                ))}
              </div>
            )}

            {/* Skill group dropdown */}
            <select
              value={selectedSkillGroup}
              onChange={(e) => setSelectedSkillGroup(e.target.value)}
              className="edit-profile-category-dropdown"
            >
              <option value="">-- Select a skill group --</option>
              {Object.keys(popularSkills).map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>

            {/* Skill chips for selected group */}
            {selectedSkillGroup && (
              <div className="edit-profile-category-chips">
                {popularSkills[selectedSkillGroup].map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    className={`edit-profile-category-chip ${
                      formData.interestedSkills.includes(skill) ? 'selected' : ''
                    }`}
                    onClick={() => toggleSkill(skill)}
                  >
                    {skill}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="button-group">
         
            <button type="button" className="cancel-button" onClick={() => navigate('/home')}>
            Cancel
            </button>
            <button type="submit" className="save-button">Update Profile</button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditProfile;
